import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { Box, Card, Dialog, Image, Typography } from '@packages/shared';
import type { FC } from 'react';
import { useState } from 'react';
import { ServiceLayer } from '@packages/modules';

const EcoLabelFragment = graphql(/* GraphQL */ `
  fragment EcoLabelFragment on EcoLabel {
    iconUrl
    text
    serviceLayerId
    license
  }
`);

export type EcoLabelProps = {
  /**
   * If `ecoLabelData['text']` is `null`, this this fallback is used instead.
   */
  fallbackHeadline?: string;

  ecoLabelData: FragmentType<typeof EcoLabelFragment>;
};

/**
 * <p>This component renders one eco-attribute of a product</p>
 * <i>(e.G.: "Bio Baumwolle", "Grüner Knopf" or "GOTS organic").</i>
 * */
export const EcoLabel: FC<EcoLabelProps> = ({ fallbackHeadline, ecoLabelData }) => {
  const { iconUrl, license, serviceLayerId, text } = getFragmentData(
    EcoLabelFragment,
    ecoLabelData,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleClick = () => {
    setIsModalOpen(true);
  };

  return (
    <>
      <Card>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',
            padding: 1.5,
            cursor: serviceLayerId ? 'pointer' : 'default',
          }}
          {...(serviceLayerId && {
            onClick: handleClick,
            role: 'button',
          })}
        >
          {iconUrl && (
            <Box
              sx={{
                width: '3.25rem',
                height: '3.25rem',
                mr: 2,
                overflow: 'hidden',
                position: 'relative',
                '> img': {
                  objectFit: 'contain',
                },
              }}
            >
              <Image alt={text || ''} src={iconUrl} unoptimized fill />
            </Box>
          )}

          <Box>
            {text && <Typography variant="body2">{text}</Typography>}
            {license && (
              <Typography variant="body3" sx={{ color: 'text.darkTransparent' }}>
                {license}
              </Typography>
            )}
          </Box>
        </Box>
      </Card>
      {serviceLayerId && (
        <Dialog
          open={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          title={text || fallbackHeadline || ''}
          dialogContent={
            <Box sx={{ p: 2 }}>
              <ServiceLayer serviceLayerId={serviceLayerId} />
            </Box>
          }
        />
      )}
    </>
  );
};
